<script>
import { mapGetters } from 'vuex'

import Header from '@components/header.vue'
import Loader from '@components/loader.vue'

export default {
  components: {
    Header,
    Loader,
  },
  props: {
    desktop: {
      type: Boolean,
      default: false,
    },
    disableHeaderLink: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    screen: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    mobile: true,
    windowWidth: 0,
  }),
  computed: {
    ...mapGetters({
      brand: 'contentful/getBrand',
      contentLoading: 'contentful/getLoading',
    }),
    isAlchemist() {
      return this.brand?.hostname === 'pay.thealchemist.uk.com'
    },
    backgroundImage() {
      return this.brand?.backgroundImage?.fields?.file?.url
    },
    leftImage() {
      return this.brand?.leftImage?.fields?.file?.url
    },
    rightImage() {
      return this.brand?.rightImage?.fields?.file?.url
    },
    documentTitle() {
      return this.brand?.documentTitle
    },
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.getWindowWidth)
      this.getWindowWidth()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.getWindowWidth)
  },
  methods: {
    getWindowWidth() {
      this.windowWidth = document.documentElement.clientWidth
      this.mobile = this.desktop ? true : this.windowWidth < 768
    },
  },
}
</script>

<template>
  <div class="app-container">
    <vue-headful :title="documentTitle" />
    <Header v-if="!hideHeader" :disable-link="disableHeaderLink" />
    <Transition name="appear">
      <div v-if="loading || contentLoading" class="loader flex center spread">
        <Loader light />
      </div>
      <div v-else key="animate-in" class="flex spread">
        <div
          v-if="mobile"
          class="flex spread background"
          :class="[{ '--default': isAlchemist }, screen]"
          :style="{
            backgroundImage: backgroundImage && `url(${backgroundImage})`,
          }"
        >
          <div v-if="isAlchemist">
            <div v-if="screen === 'home'">
              <img class="bg left" src="@assets/images/home-left.png" />
              <img class="bg right" src="@assets/images/home-right.png" />
            </div>
            <div v-if="screen === 'input'">
              <img class="bg left" src="@assets/images/input-left.png" />
              <img class="bg right" src="@assets/images/input-right.png" />
            </div>
            <div v-if="screen === 'bill'">
              <img class="bg left" src="@assets/images/bill-left.png" />
              <img class="bg right" src="@assets/images/bill-right.png" />
              <img
                class="bg bottom"
                src="@assets/images/bill-right-bottom.png"
              />
            </div>
            <div v-if="screen === 'tip'">
              <img class="bg left" src="@assets/images/tip-left.png" />
              <img class="bg right" src="@assets/images/tip-right.png" />
              <img
                class="bg middle bottom"
                src="@assets/images/tip-middle.png"
              />
            </div>
            <div v-if="screen === 'payment'" class="overlay">
              <img class="bg full left" src="@assets/images/payment-left.png" />
              <img
                class="bg full right"
                src="@assets/images/payment-right.png"
              />
            </div>
            <div v-if="screen === 'details'">
              <img class="bg full left" src="@assets/images/details-left.png" />
              <img
                class="bg full right"
                src="@assets/images/details-right.png"
              />
            </div>
            <div v-if="screen === 'failure'">
              <img
                class="bg bottom"
                src="@assets/images/bill-right-bottom.png"
              />
            </div>
          </div>
          <div v-else>
            <div v-if="screen === 'home'">
              <img v-if="leftImage" class="bg left" :src="leftImage" />
              <img v-if="rightImage" class="bg right" :src="rightImage" />
            </div>
          </div>
          <div class="layer flex">
            <slot />
          </div>
        </div>
        <div v-if="!mobile" class="mobile flex">
          <div class="flex center spread">
            <h1>Sorry, this is for mobile devices only!</h1>
            <BaseLink href="https://thealchemist.uk.com/" target="_self">
              <BaseButton>Home</BaseButton>
            </BaseLink>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
@import '@design';

.mobile {
  background-color: var(--color-white);
  height: 100vh;
}

@keyframes float {
  0% {
    transform: translateY(10px);
  }

  100% {
    transform: translateY(0);
  }
}

.loader {
  background: var(--color-white);
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: $layer-loader-z-index;
}

.bg {
  animation: float 3s ease-in-out infinite alternate-reverse;
  position: absolute;
  z-index: $layer-bg-z-index;

  &.left {
    top: 65px;
    left: 0;
  }

  &.right {
    top: 65px;
    right: 0;
  }

  &.bottom {
    bottom: -10px;
    right: 0;
    max-width: 40vw;
  }

  &.full {
    top: 70px;
    height: 320px;
  }

  &.middle {
    left: 50%;
    top: 220px;
    transform: translateX(-50%);

    &.bottom {
      top: auto;
      bottom: 120px;
      right: 0;
    }
  }
}

.background {
  &--default {
    background: url('~@assets/images/mandala.png') no-repeat center -40px,
      linear-gradient(var(--color-off-white), var(--color-white));
    background-size: center;
  }
}

.home .bg {
  max-width: 33vw;
}

.input .bg {
  max-width: 26vw;
  padding-top: 10px;
}

.bill .bg {
  &.left {
    top: 36px;
    max-width: 28vw;
  }

  &.right {
    top: 26px;
    max-width: 20vw;
  }

  &.middle {
    left: 50%;
    top: 220px;
    transform: translateX(-50%);

    &.bottom {
      top: auto;
      bottom: 150px;
      right: 0;
    }
  }
}

.tip .bg {
  &.left {
    top: 86px;
    width: 56%;
  }

  &.right {
    top: 76px;
    width: 32%;
  }

  &.middle {
    left: 50%;
    top: 220px;
    transform: translateX(-50%);

    &.bottom {
      animation: none;
      top: auto;
      bottom: 136px;
      right: 0;
    }
  }
}

.overlay .bg {
  z-index: 5;
}
</style>
